import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Upload, RefreshCcw, Trash2, CheckCircle2, ImageIcon, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("id-ID", {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

const isPosterUrl = (u) =>
  typeof u === "string" &&
  (u.includes("/api/static/posters/") || u.includes("customer-assets.emergentagent.com"));

/**
 * PostersTab — bulk-manage per-package poster images.
 * Admin drag & drop OR click to upload; deletes revert to placeholder.
 */
export default function PostersTab() {
  const { authHeaders } = useAuth();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [onlyMissing, setOnlyMissing] = React.useState(false);
  const [drag, setDrag] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/packages", { headers: authHeaders() });
      setItems(r.data);
    } catch {
      toast.error("Gagal memuat daftar paket");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  React.useEffect(() => { load(); }, [load]);

  const doUpload = async (pkgId, file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      toast.error("Format tidak didukung. Gunakan PNG/JPEG/WebP.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File terlalu besar (max 8 MB)");
      return;
    }
    setBusy(pkgId);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/admin/packages/${pkgId}/poster`, form, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Poster berhasil diupload");
      // Optimistically update this row
      setItems((prev) => prev.map((p) => (p.id === pkgId ? { ...p, image: r.data.url, has_custom_poster: true } : p)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload gagal");
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async (pkgId) => {
    if (!window.confirm("Hapus poster dan gunakan gambar default?")) return;
    setBusy(pkgId);
    try {
      const r = await api.delete(`/admin/packages/${pkgId}/poster`, { headers: authHeaders() });
      toast.success("Poster dihapus");
      setItems((prev) => prev.map((p) => (p.id === pkgId ? { ...p, image: r.data.url, has_custom_poster: false } : p)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    } finally {
      setBusy(null);
    }
  };

  const filtered = React.useMemo(() => {
    const qq = q.trim().toLowerCase();
    return items.filter((p) => {
      if (onlyMissing && p.has_custom_poster) return false;
      if (!qq) return true;
      const hay = [
        p.name_id, p.airline, p.airport, p.tier, p.duration, p.departure, p.departure_date,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(qq);
    });
  }, [items, q, onlyMissing]);

  const missingCount = items.filter((p) => !p.has_custom_poster).length;
  const totalCount = items.length;

  const resolveImg = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${API}${url}`;
  };

  return (
    <TabsContent value="posters" data-testid="tab-posters-content">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="font-serif text-2xl text-primary flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-accent" />
              Kelola Paket &amp; Poster
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Drag &amp; drop foto poster ke kartu paket, atau klik tombol <strong>Upload</strong>. Format PNG/JPEG/WebP, max 8 MB. Foto otomatis dinormalisasi ke portrait 1600px.
            </p>
            <p className="text-xs mt-2">
              <span className="font-semibold text-primary">{totalCount - missingCount}</span> / {totalCount} paket sudah punya poster ·
              <span className="text-orange-600 font-semibold"> {missingCount} belum</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <label className="inline-flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={onlyMissing}
                onChange={(e) => setOnlyMissing(e.target.checked)}
                data-testid="posters-filter-missing"
                className="rounded border-border"
              />
              Hanya belum ada poster
            </label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Cari paket..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="posters-search"
                className="pl-9 pr-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-accent w-56"
              />
            </div>
            <button
              onClick={load}
              data-testid="posters-refresh"
              className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-border hover:bg-muted transition-colors"
            >
              <RefreshCcw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground text-sm">Memuat...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground text-sm">Tidak ada paket sesuai filter.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((p) => {
              const isBusy = busy === p.id;
              const isDrag = drag === p.id;
              const hasPoster = p.has_custom_poster || isPosterUrl(p.image);
              return (
                <div
                  key={p.id}
                  data-testid={`poster-row-${p.id}`}
                  className={`border rounded-lg p-4 flex gap-4 transition-all ${
                    isDrag ? "border-accent bg-accent/10 ring-2 ring-accent" : "border-border"
                  }`}
                  onDragOver={(e) => { e.preventDefault(); setDrag(p.id); }}
                  onDragLeave={() => setDrag(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDrag(null);
                    const f = e.dataTransfer.files?.[0];
                    if (f) doUpload(p.id, f);
                  }}
                >
                  {/* Thumb */}
                  <div className="w-24 shrink-0">
                    <div className={`w-24 h-30 rounded-md overflow-hidden ring-1 ring-border bg-gradient-to-br from-slate-100 to-slate-200 ${hasPoster ? "" : "opacity-60"}`}>
                      {p.image ? (
                        <img src={resolveImg(p.image)} alt={p.name_id} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-[10px] text-muted-foreground">—</div>
                      )}
                    </div>
                    <p className="text-[10px] mt-1 text-center">
                      {hasPoster ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                          <CheckCircle2 className="h-3 w-3" /> Poster
                        </span>
                      ) : (
                        <span className="text-orange-600 font-semibold">Default</span>
                      )}
                    </p>
                  </div>

                  {/* Meta + actions */}
                  <div className="flex flex-col flex-1 min-w-0">
                    <h3 className="font-serif text-primary text-sm leading-tight line-clamp-2">
                      {p.name_id}
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                      {p.tier && <span className="font-semibold uppercase">{p.tier}</span>}
                      {p.duration && <span>· {p.duration}</span>}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {fmtDate(p.departure_date)}{p.airline ? ` · ${p.airline}` : ""}
                    </p>

                    <div className="mt-auto pt-3 flex flex-wrap gap-2">
                      <label
                        data-testid={`poster-upload-${p.id}`}
                        className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold cursor-pointer transition-colors ${
                          isBusy
                            ? "bg-muted text-muted-foreground cursor-not-allowed"
                            : "bg-primary text-white hover:bg-primary/90"
                        }`}
                      >
                        <Upload className="h-3 w-3" />
                        {isBusy ? "Mengupload..." : (hasPoster ? "Ganti" : "Upload")}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          disabled={isBusy}
                          onChange={(e) => doUpload(p.id, e.target.files?.[0])}
                        />
                      </label>
                      {hasPoster && (
                        <button
                          onClick={() => doDelete(p.id)}
                          disabled={isBusy}
                          data-testid={`poster-delete-${p.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-red-700 border border-red-200 hover:bg-red-50 disabled:opacity-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground mt-6 italic">
          Tip: seret file poster langsung ke kartu paket untuk upload cepat. Poster portrait 4:5 tampil paling optimal.
        </p>
      </div>
    </TabsContent>
  );
}
